import { useState } from 'react';
import { RichText } from './rich-text.js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useOpenPrSession } from '../hooks/use-sessions.js';
import { useUrlRow } from '../hooks/use-url-state.js';
import type { DraftReview, HeldEvent, ReviewComment, ReviewDetail, ReviewReply, ReviewSummary } from '../api/types.js';
import { useHeldEvents, usePostConversationReply, useResolveHeld, useReview, useReviews, useStartReview } from '../hooks/use-reviews.js';
import { useToast } from '../hooks/use-toast.js';
import { Button } from './button.js';
import { ConfirmDialog } from './confirm-dialog.js';
import { parseIssueRef, parseMrRef, parsePrRef, trackerWords } from '../api/pr-ref.js';
import { timeAgo } from '../utils/time-ago.js';
import { SetAsideControl } from './set-aside-form.js';

// Pull request reviews of a server (ADR-021): PRs held by the injection check, then the review proposals
// prepared in the sandbox and checked by aidev. Nothing here posts on GitHub.

const RISK = {
  high: { label: 'High risk', color: 'var(--color-error)', dim: 'var(--color-error-dim)' },
  medium: { label: 'Medium risk', color: 'var(--color-warn)', dim: 'var(--color-warn-dim)' },
  low: { label: 'Low risk', color: 'var(--color-success)', dim: 'var(--color-success-dim)' },
} as const;


function PrName({ externalRef }: { externalRef: string }) {
  const pr = parsePrRef(externalRef);
  if (!pr) return <span>{externalRef}</span>;
  return (
    <span>
      <span style={{ color: 'var(--color-text-3)' }}>{pr.org}/</span>
      <span style={{ color: 'var(--color-text)' }}>{pr.repo}</span>
      <span style={{ color: 'var(--color-text-2)' }}> #{pr.number}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Held by the injection check, or ticket comments held for their author (ADR-029 step 4)
// ---------------------------------------------------------------------------

/** The words of each kind of hold: a review the injection check stopped, or a comment from someone not trusted yet */
const HOLD_COPY = {
  screening: {
    heading: (n: number) => (n === 1 ? '1 review waits for your decision' : `${n} reviews wait for your decision`),
    intro: 'These pull requests contain text that may be aimed at the AI reviewer. Read the excerpt, then run the review anyway or drop it.',
    release: 'Run review', dismiss: 'Drop', released: 'Review started', dismissed: 'Review dropped', what: 'review',
    confirmRelease: 'Run this review?', confirmDismiss: 'Drop this review?',
    releaseMessage: (_e: HeldEvent) => 'The review session will read this pull request as it is, in the sandbox.',
    dismissMessage: 'No review will be prepared for this request.',
  },
  comment: {
    heading: (n: number) => (n === 1 ? '1 ticket comment waits for your decision' : `${n} ticket comments wait for your decision`),
    intro: 'These tickets are worked on outside the sandbox, so only comments from people you trust reach their session. Read the comment, then trust its author and deliver it, or ignore it.',
    release: 'Trust and deliver', dismiss: 'Ignore', released: 'Comment delivered', dismissed: 'Comment ignored', what: 'comment',
    confirmRelease: 'Trust and deliver this comment?', confirmDismiss: 'Ignore this comment?',
    releaseMessage: (e: HeldEvent) => `${e.author} joins the people you trust on this server, and the ticket's session reads the comment. You can revoke the trust later.`,
    dismissMessage: 'The session never sees this comment. Its author stays untrusted.',
  },
} as const;

function HeldList({ serverId, events, isAdmin, kind }: { serverId: string; events: HeldEvent[]; isAdmin: boolean; kind: keyof typeof HOLD_COPY }) {
  const copy = HOLD_COPY[kind];
  const resolve = useResolveHeld(serverId);
  const { toast } = useToast();
  const [pending, setPending] = useState<{ event: HeldEvent; action: 'release' | 'dismiss' } | null>(null);

  function confirm() {
    if (!pending) return;
    const { event, action } = pending;
    setPending(null);
    resolve.mutate({ heldId: event.id, action }, {
      onSuccess: () => toast(action === 'release' ? copy.released : copy.dismissed, 'success'),
      // Handled elsewhere while the confirmation was open (another tab, MCP): say so instead of failing
      onError: (err) => (/not found/i.test((err as Error).message)
        ? toast(`This ${copy.what} was already handled`, 'info')
        : toast(`Could not ${action === 'release' ? copy.release.toLowerCase() : copy.dismiss.toLowerCase()} the ${copy.what}: ${(err as Error).message}`, 'error')),
    });
  }

  return (
    <div className="mt-4 rounded-lg border p-4" style={{ borderColor: 'var(--color-warn)', backgroundColor: 'var(--color-warn-dim)' }}>
      <h3 className="text-sm font-semibold" style={{ color: 'var(--color-warn)' }}>
        {copy.heading(events.length)}
      </h3>
      <p className="mt-1 text-xs" style={{ color: 'var(--color-text-2)' }}>{copy.intro}</p>
      <ul className="mt-3 space-y-3">
        {events.map((event) => {
          const link = kind === 'comment' ? parseIssueRef(event.externalRef) : parsePrRef(event.externalRef);
          return (
            <li key={event.id} className="min-w-0 rounded-md border p-3" style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface)' }}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <a href={link?.url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline"><PrName externalRef={event.externalRef} /></a>
                <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>held {timeAgo(event.heldAt)}</span>
              </div>
              <p className="mt-1 text-sm" style={{ color: 'var(--color-text-2)' }}>{event.reason}</p>
              {event.excerpt && (
                <blockquote className="mt-2 rounded border-l-2 px-3 py-2 text-xs whitespace-pre-wrap break-words"
                  style={{ borderColor: 'var(--color-warn)', backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text)', fontFamily: 'var(--font-mono)' }}>
                  {event.excerpt}
                </blockquote>
              )}
              {isAdmin && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="primary" disabled={resolve.isPending} onClick={() => setPending({ event, action: 'release' })}>{copy.release}</Button>
                  <Button size="sm" variant="ghost" disabled={resolve.isPending} onClick={() => setPending({ event, action: 'dismiss' })}>{copy.dismiss}</Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={!!pending}
        title={pending?.action === 'release' ? copy.confirmRelease : copy.confirmDismiss}
        message={pending?.action === 'release' ? copy.releaseMessage(pending.event) : copy.dismissMessage}
        confirmLabel={pending?.action === 'release' ? copy.release : copy.dismiss}
        onConfirm={confirm}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review proposals
// ---------------------------------------------------------------------------

/** Mentions the checks made inert in a body (put between backticks, so GitHub notifies no one) */
function InertNote({ tokens }: { tokens?: string[] }) {
  const list = [...new Set(tokens ?? [])];
  if (!list.length) return null;
  return (
    <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
      {list.length === 1 ? '1 mention made inert' : `${list.length} mentions made inert`}, so GitHub notifies no one: {list.join(', ')}.
    </p>
  );
}

function CommentList({ comments }: { comments: ReviewComment[] }) {
  if (!comments.length) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>No line comments.</p>;
  return (
    <ol className="divide-y rounded-md border" style={{ borderColor: 'var(--color-border)' }} aria-label="Proposed comments">
      {comments.map((c, i) => (
        <li key={i} className="grid gap-x-4 gap-y-1 px-3 py-2.5 sm:grid-cols-[minmax(10rem,16rem)_1fr]" style={{ borderColor: 'var(--color-border)' }}>
          <div className="min-w-0 text-xs leading-5" style={{ fontFamily: 'var(--font-mono)' }}>
            <code className="block truncate" style={{ color: 'var(--color-text-3)' }} title={`${c.path}:${c.line}`}>{c.path}:{c.line}</code>
            {c.side === 'LEFT' && <span style={{ color: 'var(--color-error)' }}>removed line</span>}
          </div>
          <div className="min-w-0">
            <RichText text={c.body} />
            <InertNote tokens={c.neutralised} />
            {c.copiedPassage && (
              <p className="mt-1.5 rounded px-2 py-1 text-xs" style={{ backgroundColor: 'var(--color-warn-dim)', color: 'var(--color-warn)' }}>
                Check before posting: this comment copies text the session read in Jira or Confluence: “{c.copiedPassage}”.
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function DraftLine({ draft }: { draft: DraftReview }) {
  const held = draft.heldBack
    ? ` ${draft.heldBack === 1 ? '1 comment was' : `${draft.heldBack} comments were`} kept out because ${draft.heldBack === 1 ? 'it copies' : 'they copy'} Jira or Confluence text.`
    : '';
  if (draft.status === 'pending') {
    return (
      <p className="rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-text)' }}>
        Draft review on GitHub with {draft.comments === 1 ? '1 comment' : `${draft.comments ?? 0} comments`}
        {draft.replies ? ` and ${draft.replies === 1 ? '1 reply' : `${draft.replies} replies`}` : ''}, visible only to you until you submit it.{held}{' '}
        {draft.url && <a href={draft.url} target="_blank" rel="noreferrer" className="font-medium underline" style={{ color: 'var(--color-brand-light)' }}>Open the draft</a>}
      </p>
    );
  }
  if (draft.status === 'withdrawn') {
    return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>The draft review was deleted from GitHub: {draft.reason}.</p>;
  }
  return (
    <p className="text-sm" style={{ color: draft.status === 'failed' ? 'var(--color-error)' : 'var(--color-text-3)' }}>
      {draft.status === 'failed' ? 'The draft review could not be created on GitHub' : 'No draft review on GitHub'}: {draft.reason}.{held}
    </p>
  );
}

function ReplyList({ replies }: { replies: ReviewReply[] }) {
  return (
    <ul className="space-y-3">
      {replies.map((r) => (
        <li key={r.inReplyTo} className="rounded-md border p-3" style={{ borderColor: 'var(--color-border)' }}>
          <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
            <code style={{ fontFamily: 'var(--font-mono)' }}>{r.path ? `${r.path}${r.line ? `:${r.line}` : ''}` : `thread ${r.inReplyTo}`}</code>
            {r.answering && <>, answering {r.answering.author}</>}
          </p>
          {r.answering && (
            <blockquote className="mt-1 border-l-2 pl-2 text-xs whitespace-pre-wrap" style={{ borderColor: 'var(--color-border-2)', color: 'var(--color-text-2)' }}>
              {r.answering.body}
            </blockquote>
          )}
          <RichText text={r.body} className="mt-2" />
          <InertNote tokens={r.neutralised} />
          {r.copiedPassage && (
            <p className="mt-2 text-xs" style={{ color: 'var(--color-warn)' }}>
              Kept out of the draft: this reply copies text the session read in Jira or Confluence: “{r.copiedPassage}”
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Opens the operator's own session on the PR, or the sandboxed session that prepared the review */
function SessionActions({ serverId, externalRef, reviewSessionId, canOpen, serverRunning, url }: {
  serverId: string; externalRef: string; reviewSessionId: string; canOpen: boolean; serverRunning: boolean; url?: string;
}) {
  const navigate = useNavigate();
  const open = useOpenPrSession(serverId);
  const { toast } = useToast();
  const mr = parseMrRef(externalRef);
  const pr = parsePrRef(externalRef) ?? (mr ? { ...mr, ...(url ? { url } : {}) } : null);
  return (
    <div className="flex flex-wrap items-center gap-3">
      {canOpen && pr && (
        <Button size="sm" variant="primary" disabled={!serverRunning || open.isPending}
          title={serverRunning ? undefined : 'Start the server to open a session'}
          onClick={() => open.mutate(pr, {
            onSuccess: (session) => navigate(`/servers/${serverId}/sessions/${session.id}`),
            onError: (err) => toast(`Could not open a session: ${(err as Error).message}`, 'error'),
          })}>
          {open.isPending ? 'Opening…' : `Open a session on this ${trackerWords(externalRef).short}`}
        </Button>
      )}
      {canOpen && pr && !serverRunning && <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>Start the server to open a session.</span>}
      <Link to={`/servers/${serverId}/sessions/${reviewSessionId}`} className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>
        View the review session
      </Link>
    </div>
  );
}

/**
 * The reply the session drafted in the PR's conversation, when the operator was mentioned there: he edits it and posts
 * it under his name from here (nothing else of a review is posted by aidev); once posted, its link.
 */
function ConversationReply({ serverId, data, isAdmin }: { serverId: string; data: ReviewDetail; isAdmin: boolean }) {
  const reply = data.proposal?.conversation;
  const post = usePostConversationReply(serverId, data.externalRef);
  const [body, setBody] = useState(reply?.body ?? '');
  if (!reply) return null;
  const posted = data.conversationPosted;
  const open = (data.prState ?? 'open') === 'open';
  return (
    <div>
      <h4 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Proposed reply in the conversation</h4>
      {posted ? (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-2)' }}>
          Posted under your name{' '}
          <a href={posted.url} target="_blank" rel="noreferrer" className="font-medium underline" style={{ color: 'var(--color-brand-light)' }}>in the conversation</a>.
        </p>
      ) : isAdmin && open ? (
        <form className="mt-1 space-y-2" onSubmit={(e) => { e.preventDefault(); post.mutate(body.trim()); }} aria-label="Reply in the conversation">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={2000} aria-label="Your reply in the conversation"
            className="block w-full rounded-md px-2.5 py-1.5 text-sm font-[inherit] leading-relaxed focus:outline-none focus-visible:outline focus-visible:outline-2"
            style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' }} />
          <InertNote tokens={reply.neutralised} />
          {reply.copiedPassage && (
            <p className="rounded px-2 py-1 text-xs" style={{ backgroundColor: 'var(--color-warn-dim)', color: 'var(--color-warn)' }}>
              Check before posting: this reply copies text the session read in Jira or Confluence: “{reply.copiedPassage}”.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!body.trim() || post.isPending}>{post.isPending ? 'Posting the reply…' : 'Post the reply'}</Button>
            <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>Posted on the pull request under your name, as written here.</span>
          </div>
          {post.error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not posted: {(post.error as Error).message}</p>}
        </form>
      ) : (
        <RichText text={reply.body} className="mt-1" />
      )}
    </div>
  );
}

function ReviewDetailPanel({ serverId, externalRef, isAdmin, serverRunning }: { serverId: string; externalRef: string; isAdmin: boolean; serverRunning: boolean }) {
  const { data, isLoading, isError, error } = useReview(serverId, externalRef);
  if (isLoading) return <p className="px-4 pb-4 text-sm" style={{ color: 'var(--color-text-3)' }}>Loading the proposal...</p>;
  if (isError || !data) return <p className="px-4 pb-4 text-sm" style={{ color: 'var(--color-error)' }}>Could not load the proposal: {(error as Error)?.message}</p>;
  const p = data.proposal;
  return (
    <div className="space-y-4 px-4 pb-4">
      {data.errors.length > 0 && (
        <div className="rounded-md px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>
          {p ? 'Kept with warnings: ' : 'This proposal was not kept: '}{data.errors.join('; ')}.
        </div>
      )}
      {data.draft && <DraftLine draft={data.draft} />}
      {isAdmin && data.draft?.status === 'pending' && (
        <div className="flex flex-wrap items-center gap-2"><SetAsideControl serverId={serverId} itemId={`review:${externalRef}`} /></div>
      )}
      {p && (
        <>
          <div>
            <h4 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Summary</h4>
            <RichText text={p.summaryFr} className="mt-1" />
          </div>
          <div>
            <h4 className="mb-2 text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Proposed comments</h4>
            <CommentList comments={p.comments} />
          </div>
          {(p.replies?.length ?? 0) > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Proposed replies</h4>
              <ReplyList replies={p.replies!} />
            </div>
          )}
          <ConversationReply serverId={serverId} data={data} isAdmin={isAdmin} />
          {p.questions.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Questions for the author</h4>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-sm" style={{ color: 'var(--color-text)' }}>
                {p.questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
      {data.rejected.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>
            Dropped by the checks ({data.rejected.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
            {data.rejected.map((r) => (
              <li key={`${r.reply ? 'r' : 'c'}${r.index}`}>
                <code style={{ fontFamily: 'var(--font-mono)' }}>{r.conversation ? 'reply in the conversation' : r.path ? `${r.path}:${r.line ?? '?'}` : `${r.reply ? 'reply' : 'comment'} ${r.index + 1}`}</code>
                {r.reply ? ' (reply)' : ''}: {r.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
      <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        Reviewed at commit <code style={{ fontFamily: 'var(--font-mono)' }}>{data.headSha.slice(0, 12)}</code>.
      </p>
      <SessionActions serverId={serverId} externalRef={externalRef} reviewSessionId={data.sessionId} canOpen={isAdmin} serverRunning={serverRunning} url={data.draft?.url} />
    </div>
  );
}

/** Urgent reasons: what puts a review at the top */
const URGENT = /^(expedite|overdue)/;

/** The PR's tickets and why it ranks where it does, under its title: outside the row's button, the keys being links */
function TicketLine({ review }: { review: ReviewSummary }) {
  return (
    <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-4 pb-3 pl-[2.6rem] text-xs" style={{ color: 'var(--color-text-3)' }} data-testid="review-tickets">
      {review.tickets?.map((t) => (
        <a key={t.key} href={t.url} target="_blank" rel="noreferrer" title={t.title} className={`font-medium underline-offset-2 hover:underline${t.done ? ' line-through' : ''}`}
          style={{ color: 'var(--color-brand-light)' }}>{t.key}</a>
      ))}
      {review.priority?.length ? (
        <span>{review.priority.map((r, i) => (
          <span key={r} style={URGENT.test(r) ? { color: 'var(--color-warn)', fontWeight: 500 } : undefined}>{i ? `, ${r}` : r}</span>
        ))}</span>
      ) : null}
    </p>
  );
}

function ReviewRow({ serverId, review, isAdmin, serverRunning }: { serverId: string; review: ReviewSummary; isAdmin: boolean; serverRunning: boolean }) {
  const { open, toggle, ref } = useUrlRow<HTMLLIElement>('review', review.externalRef);
  const risk = review.valid && review.risk ? RISK[review.risk] : null;
  const pr = parsePrRef(review.externalRef);
  const panelId = `review-${review.externalRef.replace(/[^a-z0-9]/gi, '-')}`;
  return (
    <li ref={ref} id={`review-row-${review.externalRef}`} className="card overflow-hidden" style={{ borderLeft: `3px solid ${risk?.color ?? 'var(--color-border-2)'}` }}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
        <button type="button" aria-expanded={open} aria-controls={panelId} onClick={toggle}
          className="flex min-w-0 flex-1 basis-full items-center gap-3 rounded text-left focus-visible:outline focus-visible:outline-2 sm:basis-auto">
          <svg className="h-3.5 w-3.5 flex-shrink-0 transition-transform motion-reduce:transition-none" style={{ color: 'var(--color-text-3)', transform: open ? 'rotate(90deg)' : 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
          <span className="min-w-0">
            <span className="flex min-w-0 items-baseline gap-2 text-sm font-medium">
              <span className="truncate"><PrName externalRef={review.externalRef} /></span>
              {review.prAuthor && <span className="flex-shrink-0 text-xs font-normal" style={{ color: 'var(--color-text-3)' }}>by {review.prAuthor}</span>}
            </span>
            {review.prTitle && <span className="block truncate text-xs" style={{ color: 'var(--color-text-2)' }}>{review.prTitle}</span>}
          </span>
          {risk ? (
            <span className="flex-shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium" style={{ backgroundColor: risk.dim, color: risk.color }}>{risk.label}</span>
          ) : (
            <span className="flex-shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium" style={{ backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>Not kept</span>
          )}
          <span className="hidden text-xs sm:inline" style={{ color: 'var(--color-text-3)' }}>
            {review.comments === 1 ? '1 comment' : `${review.comments} comments`}
            {review.rejected > 0 && `, ${review.rejected} dropped`}
          </span>
        </button>
        {review.prState && review.prState !== 'open' && (
          <span className="flex-shrink-0 rounded px-1.5 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-idle-dim)', color: 'var(--color-text-2)' }}>
            {review.prState === 'merged' ? 'Merged' : 'Closed'}
          </span>
        )}
        <span className="flex-shrink-0 pl-6 text-xs sm:pl-0" style={{ color: 'var(--color-text-3)' }}>{timeAgo(review.collectedAt)}</span>
        {review.draft?.status === 'pending' && review.draft.url ? (
          <a href={review.draft.url} target="_blank" rel="noreferrer" className="flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium hover:underline"
            style={{ backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-brand-light)' }}>
            Draft on GitHub
          </a>
        ) : pr && (
          <a href={pr.url} target="_blank" rel="noreferrer" className="flex-shrink-0 text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>
            Open on GitHub
          </a>
        )}
      </div>
      {(review.tickets?.length || review.priority?.length) ? <TicketLine review={review} /> : null}
      {open && <div id={panelId}><ReviewDetailPanel serverId={serverId} externalRef={review.externalRef} isAdmin={isAdmin} serverRunning={serverRunning} /></div>}
    </li>
  );
}

/** Open reviews listed before « Show all » */
export const REVIEWS_FIRST = 5;

/**
 * A review of any pull request, on demand (ADR-039 step 2): prepared as when the operator's review is requested,
 * whoever it was asked of. Admins only, as the other decisions here.
 */
function StartReview({ serverId }: { serverId: string }) {
  const [pr, setPr] = useState('');
  const start = useStartReview(serverId);
  return (
    <form className="mt-3 flex max-w-xl flex-wrap items-center gap-2" aria-label="Review a pull request"
      onSubmit={(e) => { e.preventDefault(); if (pr.trim()) start.mutate(pr.trim(), { onSuccess: () => setPr('') }); }}>
      <label htmlFor={`start-review-${serverId}`} className="sr-only">Pull request to review</label>
      <input id={`start-review-${serverId}`} value={pr} onChange={(e) => { setPr(e.target.value); if (start.isSuccess || start.isError) start.reset(); }} maxLength={200} placeholder="org/repo#123 or the pull request's URL"
        className="min-w-0 flex-1 rounded-md border px-2 py-1 text-sm"
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }} />
      <Button size="sm" type="submit" disabled={!pr.trim() || start.isPending}>{start.isPending ? 'Starting…' : 'Review it'}</Button>
      <span aria-live="polite" className="basis-full text-xs" style={{ color: 'var(--color-text-3)' }}>
        {start.isSuccess ? `Review of ${start.data.externalRef.replace(/^github:/, '')} started: its proposal shows here once the server has prepared it.` : ''}
      </span>
      {start.isError && <p role="alert" className="basis-full text-xs" style={{ color: 'var(--color-error)' }}>Not started: {(start.error as Error).message}</p>}
    </form>
  );
}

export function PrReviews({ serverId, isAdmin, serverRunning = false }: { serverId: string; isAdmin: boolean; serverRunning?: boolean }) {
  const reviews = useReviews(serverId);
  const [params] = useSearchParams();
  const [all, setAll] = useState(false);
  const held = useHeldEvents(serverId);
  const reviewList = reviews.data ?? [];
  const heldList = held.data ?? [];
  const heldReviews = heldList.filter((e) => !e.author);
  const heldComments = heldList.filter((e) => e.author);
  const loadError = (reviews.error ?? held.error) as Error | null | undefined;
  // Servers that do not review PRs get no empty section; a failed load is shown, not hidden as empty
  // Admins can start a review even before the first one: the section shows for them
  if (!reviewList.length && !heldList.length && !loadError && !isAdmin) return null;
  // A server publishing drafts records a draft state (pending, skipped or failed) on each collected review
  const draftsOn = reviewList.some((r) => r.draft);
  // Merged or closed PRs need nothing more from the operator: kept apart, folded
  const openReviews = reviewList.filter((r) => !r.prState || r.prState === 'open');
  const closedReviews = reviewList.filter((r) => r.prState && r.prState !== 'open');
  // A link to a review further down shows them all, so the row it opens is there
  const target = params.get('review');
  const targetHidden = !!target && openReviews.findIndex((r) => r.externalRef === target) >= REVIEWS_FIRST;
  const showAll = all || targetHidden || openReviews.length <= REVIEWS_FIRST + 1;
  const shownReviews = showAll ? openReviews : openReviews.slice(0, REVIEWS_FIRST);
  return (
    <section aria-labelledby="pr-reviews-title" className="mb-8 min-w-0">
      <h2 id="pr-reviews-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Pull request reviews</h2>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>
        Prepared in the sandbox and checked by aidev before you read them, the most urgent tickets first.{' '}
        {draftsOn ? 'Draft reviews on GitHub stay visible only to you until you submit them.' : 'Nothing is posted on GitHub.'}
      </p>
      {isAdmin && <StartReview serverId={serverId} />}
      {loadError && (
        <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--color-error)' }}>
          Could not load the pull request reviews: {loadError.message}. They refresh every minute.
        </p>
      )}
      {heldReviews.length > 0 && <HeldList serverId={serverId} events={heldReviews} isAdmin={isAdmin} kind="screening" />}
      {heldComments.length > 0 && <HeldList serverId={serverId} events={heldComments} isAdmin={isAdmin} kind="comment" />}
      {openReviews.length > 0 && (
        <ul className="mt-4 space-y-2">
          {shownReviews.map((r) => <ReviewRow key={r.externalRef} serverId={serverId} review={r} isAdmin={isAdmin} serverRunning={serverRunning} />)}
        </ul>
      )}
      {shownReviews.length < openReviews.length && (
        <button type="button" onClick={() => setAll(true)}
          className="mt-2 py-1 text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
          Show all {openReviews.length} open pull requests
        </button>
      )}
      {reviewList.length > 0 && !openReviews.length && (
        <p className="mt-4 text-sm" style={{ color: 'var(--color-text-2)' }}>No open pull request waits for your review.</p>
      )}
      {closedReviews.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Merged or closed ({closedReviews.length})</summary>
          <ul className="mt-2 space-y-2">
            {closedReviews.map((r) => <ReviewRow key={r.externalRef} serverId={serverId} review={r} isAdmin={isAdmin} serverRunning={serverRunning} />)}
          </ul>
        </details>
      )}
    </section>
  );
}
